import { useState } from 'react';
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { BuildOutlined, DeleteOutlined, PauseOutlined, PlayArrowOutlined, ReplayOutlined } from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';
import { useNotifications } from '@/hooks/use-notifications';
import {
  useDeleteSingleCamAgentMutation,
  useSingleCamAgentMaintenanceMutation,
  useSingleCamAgentPauseMutation,
  useSingleCamAgentResumeMutation,
} from '@/state/mutations/singlecam-agent-admin/use-singlecam-agent-lifecycle-mutations';
import { useForceReleaseSingleCamAgentMutation } from '@/state/mutations/singlecam-agent-admin/use-force-release-singlecam-agent';
import type { SingleCamAgentPoolMemberDto } from '@/types/dtos/singlecam-agent/singlecam-agent-pool.dto';

function vmDisplayStatus(vm: SingleCamAgentPoolMemberDto['vms'][number]): string | undefined {
  if (vm.activationStatus === 'deployed' && vm.vmStatus) {
    return vm.vmStatus;
  }
  if (vm.activationStatus && vm.vmStatus && vm.activationStatus.toLowerCase() === vm.vmStatus.toLowerCase()) {
    return vm.activationStatus;
  }
  if (vm.activationStatus && vm.vmStatus) {
    return `${vm.activationStatus} · ${vm.vmStatus}`;
  }
  return vm.activationStatus ?? vm.vmStatus;
}

export function SingleCamAgentVmCell({ agent }: { agent: SingleCamAgentPoolMemberDto }) {
  if (!agent.vms?.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <>
      {agent.vms.map((vm) => {
        const status = vmDisplayStatus(vm);
        return (
          <Stack key={vm.vmId} direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {vm.vmId}
            </Typography>
            {status && <Chip size="small" label={status} variant="outlined" />}
          </Stack>
        );
      })}
    </>
  );
}

type ConfirmAction = 'maintenance' | 'pause' | 'forceRelease' | 'delete';

export default function SingleCamAgentLifecycleActions({ agent }: { agent: SingleCamAgentPoolMemberDto }) {
  const t = useTranslations();
  const notification = useNotifications();
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

  const { mutateAsync: enterMaintenance, isPending: isMaintenance } = useSingleCamAgentMaintenanceMutation();
  const { mutateAsync: pause, isPending: isPausing } = useSingleCamAgentPauseMutation();
  const { mutateAsync: resume, isPending: isResuming } = useSingleCamAgentResumeMutation();
  const { mutateAsync: deleteAgent, isPending: isDeleting } = useDeleteSingleCamAgentMutation();
  const { mutateAsync: forceRelease, isPending: isReleasing } = useForceReleaseSingleCamAgentMutation();

  const busy = isMaintenance || isPausing || isResuming || isDeleting || isReleasing;
  const isAllocated = Boolean(agent.allocatedStagePlayerId);
  const opsStatus = agent.opsStatus ?? 'active';
  const isAzure = agent.deployment === 'azure';

  const canEnterMaintenance = opsStatus === 'active' && !isAllocated;
  const canPause = isAzure && opsStatus === 'active' && !isAllocated;
  const canResume = opsStatus === 'maintenance' || opsStatus === 'paused';
  const canDelete = !isAllocated;
  const canForceRelease = isAllocated;

  const closeConfirm = () => setConfirmAction(null);

  const onEnterMaintenance = async () => {
    try {
      await enterMaintenance(agent.playerId);
      notification.notifySuccess(
        t('admin.singlecamAgentAdmin.maintenanceEnabled', undefined, 'Maintenance mode enabled'),
      );
      closeConfirm();
    } catch {
      notification.notifyError(
        t('admin.singlecamAgentAdmin.maintenanceError', undefined, 'Failed to update maintenance mode'),
      );
    }
  };

  const onPause = async () => {
    try {
      await pause(agent.playerId);
      notification.notifySuccess(t('admin.singlecamAgentAdmin.pauseSuccess', undefined, 'Agent paused'));
      closeConfirm();
    } catch {
      notification.notifyError(t('admin.singlecamAgentAdmin.pauseError', undefined, 'Failed to pause agent'));
    }
  };

  const onResume = async () => {
    try {
      await resume(agent.playerId);
      notification.notifySuccess(t('admin.singlecamAgentAdmin.resumeSuccess', undefined, 'Agent resumed'));
    } catch {
      notification.notifyError(t('admin.singlecamAgentAdmin.resumeError', undefined, 'Failed to resume agent'));
    }
  };

  const onForceRelease = async () => {
    try {
      const result = await forceRelease(agent.playerId);
      notification.notifySuccess(
        result.released
          ? t(
              'admin.singlecamAgentAdmin.forceReleaseSuccess',
              undefined,
              'Allocation released and stage player re-queued',
            )
          : t('admin.singlecamAgentAdmin.forceReleaseNoop', undefined, 'No active allocation found'),
      );
      closeConfirm();
    } catch {
      notification.notifyError(t('admin.singlecamAgentAdmin.forceReleaseError', undefined, 'Failed to force release'));
    }
  };

  const onDelete = async () => {
    try {
      await deleteAgent(agent.playerId);
      notification.notifySuccess(t('admin.singlecamAgentAdmin.deleteSuccess', undefined, 'Agent deleted'));
      closeConfirm();
    } catch {
      notification.notifyError(t('admin.singlecamAgentAdmin.deleteError', undefined, 'Failed to delete agent'));
    }
  };

  const confirmPending = () => {
    switch (confirmAction) {
      case 'maintenance':
        return void onEnterMaintenance();
      case 'pause':
        return void onPause();
      case 'forceRelease':
        return void onForceRelease();
      case 'delete':
        return void onDelete();
      default:
        return;
    }
  };

  const confirmTitle =
    confirmAction === 'maintenance'
      ? t('admin.singlecamAgentAdmin.maintenanceConfirmTitle', undefined, 'Enter maintenance?')
      : confirmAction === 'pause'
        ? t('admin.singlecamAgentAdmin.pauseConfirmTitle', undefined, 'Pause agent?')
        : confirmAction === 'forceRelease'
          ? t('admin.singlecamAgentAdmin.forceReleaseConfirmTitle', undefined, 'Force release allocation?')
          : t('admin.singlecamAgentAdmin.deleteConfirmTitle', undefined, 'Delete agent?');

  const confirmText =
    confirmAction === 'maintenance'
      ? t(
          'admin.singlecamAgentAdmin.maintenanceConfirmText',
          undefined,
          'Put this single-cam agent into maintenance? It will stop accepting new allocations until resumed.',
        )
      : confirmAction === 'pause'
        ? t(
            'admin.singlecamAgentAdmin.pauseConfirmText',
            undefined,
            'Pause this single-cam agent? The Azure VM will be stopped and removed from the pool until resumed.',
          )
        : confirmAction === 'forceRelease'
          ? t(
              'admin.singlecamAgentAdmin.forceReleaseConfirmText',
              undefined,
              'Force-release the current allocation? The stage player will be re-queued and any live session on this agent may be interrupted.',
            )
          : t(
              'admin.singlecamAgentAdmin.deleteConfirmText',
              undefined,
              'Delete this single-cam agent and its VM record? This cannot be undone.',
            );

  const confirmLabel =
    confirmAction === 'maintenance'
      ? t('admin.singlecamAgentAdmin.enterMaintenance', undefined, 'Enter maintenance')
      : confirmAction === 'pause'
        ? t('admin.singlecamAgentAdmin.pause', undefined, 'Pause')
        : confirmAction === 'forceRelease'
          ? t('admin.singlecamAgentAdmin.forceRelease', undefined, 'Force release')
          : t('admin.singlecamAgentAdmin.delete', undefined, 'Delete');

  const confirmPendingBusy =
    (confirmAction === 'maintenance' && isMaintenance) ||
    (confirmAction === 'pause' && isPausing) ||
    (confirmAction === 'forceRelease' && isReleasing) ||
    (confirmAction === 'delete' && isDeleting);

  const confirmIsDestructive =
    confirmAction === 'delete' || confirmAction === 'forceRelease' || confirmAction === 'pause';

  return (
    <>
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="nowrap" useFlexGap>
        {canEnterMaintenance && (
          <Tooltip title={t('admin.singlecamAgentAdmin.enterMaintenance', undefined, 'Enter maintenance')}>
            <span>
              <IconButton size="small" onClick={() => setConfirmAction('maintenance')} disabled={busy}>
                {isMaintenance ? <CircularProgress size={18} /> : <BuildOutlined fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
        {canPause && (
          <Tooltip title={t('admin.singlecamAgentAdmin.pause', undefined, 'Pause')}>
            <span>
              <IconButton size="small" onClick={() => setConfirmAction('pause')} disabled={busy}>
                {isPausing ? <CircularProgress size={18} /> : <PauseOutlined fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
        {canResume && (
          <Tooltip title={t('admin.singlecamAgentAdmin.resume', undefined, 'Resume')}>
            <span>
              <IconButton size="small" onClick={() => void onResume()} disabled={busy}>
                {isResuming ? <CircularProgress size={18} /> : <PlayArrowOutlined fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
        {canForceRelease && (
          <Tooltip title={t('admin.singlecamAgentAdmin.forceRelease', undefined, 'Force release allocation')}>
            <span>
              <IconButton size="small" onClick={() => setConfirmAction('forceRelease')} disabled={busy} color="warning">
                {isReleasing ? <CircularProgress size={18} /> : <ReplayOutlined fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip
          title={
            !canDelete
              ? t('admin.singlecamAgentAdmin.deleteBlockedAllocated', undefined, 'Deallocate before delete')
              : t('admin.singlecamAgentAdmin.deleteHint', undefined, 'Delete agent')
          }
        >
          <span>
            <IconButton
              size="small"
              onClick={() => setConfirmAction('delete')}
              disabled={busy || !canDelete}
              color="error"
            >
              <DeleteOutlined fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Dialog open={confirmAction !== null} onClose={closeConfirm}>
        <DialogTitle>{confirmTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmText}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm}>{t('common.cancel', undefined, 'Cancel')}</Button>
          <Button
            variant="contained"
            color={confirmIsDestructive ? 'error' : 'warning'}
            onClick={confirmPending}
            disabled={confirmPendingBusy}
          >
            {confirmPendingBusy ? <CircularProgress size={20} color="inherit" /> : confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
